/**
 * THE CONTACT US IMAGE TREATMENTS — Layne's markup, applied per division (2026-09-02).
 *
 * `MARKUP-CONTACT-US-TREATMENTS-20260902`: a Contact Us block that declares a data
 * source carries one of three image treatments in its DE row — `none`, `group`, or
 * `headshots` — and the two that draw something replace the CONTENTS of the block's
 * image-region table, the one holding the 80×53 "We're Here to Help You" icon:
 *
 *   <table cellpadding="0" cellspacing="0" role="presentation" style="margin-bottom: 15px;" width="100%">
 *
 * Both fragments below are hers, verbatim. The two things she asked this side to own:
 *
 *   1. "render only populated headshot slots (omit the <td> entirely for an empty one)"
 *   2. "substitute {{HeadshotSize}} per the count table" — 100 for 1–2, 90 for 3,
 *      85 for 4, 80 for 5. Her note: the 20px gap between slots is load-bearing at
 *      600px wide, so the padding is not touched here and never will be.
 *
 * ── TOKENS ARE MATCHED BY NAME, LOOSELY ────────────────────────────────────────────
 * Her fragments write `{{Headshot1Url}}`; the DE column is `headshot1_url` (the rowset
 * API lowercases every name) and the header's declared field key is whatever casing she
 * chose. All three are the same name, so a token resolves by comparing names with case
 * and underscores stripped — `Headshot1Url` ≡ `headshot1_url` ≡ `Headshot1_Url`. The
 * block's OWN DATA is consulted first (a requester's typed value, or the fill's), the
 * DE row second, so a panel edit shows on the canvas and an undeclared column (the group
 * image's width and height) still resolves.
 *
 * ── ALWAYS FROM THE PRISTINE MARKUP ─────────────────────────────────────────────────
 * The region is rewritten from the block's untouched copy (`__html0`, the same base the
 * listing table keeps), never from the last pass's output: a `headshots` row that later
 * changes to `group`, or a division switch from five slots to two, has to re-cut cleanly,
 * and a lazy `</table>` match over an already-nested fragment would close on the inner
 * table. `none` returns the base unchanged.
 *
 * ── EMPTY MEANS HIDE (Layne) ───────────────────────────────────────────────────────
 * `group` with no image URL, or `headshots` with no populated slot, removes the region
 * table outright rather than drawing a broken image or an empty box — and never falls
 * back to the icon, which would be a substitution. Alt text is rendered with whatever
 * exists, empty included; the missing ones are COUNTED and surfaced by
 * `contactAltGaps` for the block-wide warnings list, per her ruling: "render it with
 * whatever alt exists and surface '3 headshots missing alt text' in the block-wide
 * warnings rather than holding the image back."
 */
import { escML } from "../email/core";

/** `Headshot1Url`, `headshot1_url`, `Headshot1_Url` → `headshot1url` */
export const normTok = (s: unknown): string => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * How a token, DE column or field name is looked up — data first, then the DE row.
 *
 * `def` is the field's DECLARED default, and it is deliberately a SEPARATE door rather
 * than the last step of `get` (2026-09-09). Layne's "empty means hide" ruling is about
 * what the ROW answered: `contactHeadshotSlots` and the `hide` test must see a blank
 * column as blank, or a field that ever declares a default image URL would draw a slot
 * the division does not have. So the default is reachable only where it belongs — the
 * final token substitution, where a blank answer would otherwise ship a raw `{{Token}}`.
 */
export type TokLookup = ((name: string) => string) & { def?: (name: string) => string };

/**
 * Build the lookup from the block's data and the division's DE row (either may be
 * missing). Data wins when it holds a NON-EMPTY value for the name; an empty data value
 * still falls through to the row, so a fed field that has not been written yet does not
 * hide a column that has an answer. Only string/number values count as data — the
 * block's own `__html`, `__socFed` and friends are skipped by the `__` prefix.
 */
export function contactLookup(
  data: Record<string, unknown> | null | undefined,
  row: Record<string, string> | null | undefined,
  fields?: Record<string, { fromColumn?: string; default?: unknown }> | null,
): TokLookup {
  const d: Record<string, string> = {};
  if (data) for (const k of Object.keys(data)) {
    if (k.startsWith("__")) continue;
    const v = data[k];
    if (typeof v === "string" || typeof v === "number") d[normTok(k)] = String(v);
  }
  const r: Record<string, string> = {};
  if (row) for (const k of Object.keys(row)) r[normTok(k)] = String(row[k] ?? "");
  /* ── THE DECLARED `fromColumn` IS THE MECHANISM; the name match is the fallback
     (Layne, REPLY-JACK-CONTACT-US-20260902b). `ContactUrl` is declared with
     `fromColumn: "Cta1_Url"` so the block's existing `{{ContactUrl}}` token did not have
     to change — normalised, `contacturl` and `cta1url` never meet, so a resolver that
     only matched names would fill every headshot and lose the one link the block exists
     for. So a field's row value is reachable under the FIELD's name via its declared
     column, before any name-based guess. */
  const viaColumn: Record<string, string> = {};
  if (fields) for (const k of Object.keys(fields)) {
    const col = String((fields[k] && fields[k].fromColumn) || "").trim();
    if (col) {
      viaColumn[normTok(k)] = normTok(col);
      /* ── AND THE REVERSE, SO A COLUMN NAME SEES A TYPED VALUE (2026-09-09) ──────
         `IFCOLUMN:Cta1_Url` asks about a COLUMN; the requester's typed value lives
         under the FIELD name (`ContactUrl`). Without this, a division that typed a
         link into an empty-column row would have its region deleted anyway — the
         same `contacturl` ≠ `cta1url` trap that bit the CTA work. Data still wins
         only when non-empty, so this never masks a real column value. */
      const typed = d[normTok(k)];
      if (typed !== undefined && typed.trim() !== "" && !d[normTok(col)]) d[normTok(col)] = typed;
    }
  }
  const defaults: Record<string, string> = {};
  if (fields) for (const k of Object.keys(fields)) {
    const dv = fields[k] && fields[k].default;
    if (typeof dv === "string" || typeof dv === "number") defaults[normTok(k)] = String(dv);
  }
  const get: TokLookup = (name) => {
    const n = normTok(name);
    const dv = d[n];
    if (dv !== undefined && dv.trim() !== "") return dv.trim();
    const col = viaColumn[n];
    if (col !== undefined && r[col] !== undefined) return r[col].trim();
    const rv = r[n];
    return rv === undefined ? "" : rv.trim();
  };
  get.def = (name) => {
    const v = defaults[normTok(name)];
    return v === undefined ? "" : v.trim();
  };
  return get;
}

export type ContactTreatment = "none" | "group" | "headshots";

/** the row's `image_treatment`, normalised; anything unrecognised is `none` */
export function contactTreatmentOf(get: TokLookup): ContactTreatment {
  const t = get("image_treatment").toLowerCase();
  return t === "group" || t === "headshots" ? t : "none";
}

/** the image-region table: the ONE table in the block styled `margin-bottom: 15px` */
export const CONTACT_IMAGE_REGION_RE =
  /(<table\b[^>]*\bstyle="\s*margin-bottom:\s*15px;?\s*"[^>]*>)([\s\S]*?)(<\/table>)/i;
const CONTACT_IMAGE_REGION_RE_G =
  /(<table\b[^>]*\bstyle="\s*margin-bottom:\s*15px;?\s*"[^>]*>)([\s\S]*?)(<\/table>)/gi;

/**
 * Layne's explicit image-region markers (BUG-CONTACTUS-RENDER-20260908) — when the block
 * carries them, they are the anchor and nothing else is guessed at.
 */
export const CONTACT_IMAGES_BEGIN = /<!--\s*BEGIN Com contact images\s*-->/i;
export const CONTACT_IMAGES_END = /<!--\s*END Com contact images\s*-->/i;

/**
 * Where the image region is in `html`: `[start, end]` of the span to replace, plus
 * the text to put back around the new inner markup.
 *
 * ── THE ANCHOR IS THE INNERMOST TABLE THAT HOLDS THE ICON (2026-09-08) ─────────────
 * Layne: "We're Here to Help You no longer renders… our read is that the replacement is
 * scoped wider than that one table — the three #F0EDE0 wrappers look alike and are easy
 * to mistake for each other." A single lazy match takes the FIRST table styled
 * `margin-bottom: 15px`; if a wrapper ever carries that style too, its match closes on
 * the icon table's `</table>`, the wrapper's own open tag is consumed, and the headline
 * table that followed is left inside a broken pair. So: (1) her explicit
 * `BEGIN/END Com contact images` markers win outright when present; (2) otherwise every
 * `margin-bottom: 15px` table is tried and the one chosen is the one whose contents hold
 * an `<img>` and NO nested `<table>` — the icon's own table, never a wrapper.
 */
export function contactImageRegion(html: string): { start: number; end: number; open: string; close: string } | null {
  const b = CONTACT_IMAGES_BEGIN.exec(html);
  if (b) {
    const after = html.slice(b.index + b[0].length);
    const e = CONTACT_IMAGES_END.exec(after);
    if (e) return { start: b.index + b[0].length, end: b.index + b[0].length + e.index, open: "", close: "" };
  }
  CONTACT_IMAGE_REGION_RE_G.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CONTACT_IMAGE_REGION_RE_G.exec(html))) {
    if (/<img\b/i.test(m[2]) && !/<table\b/i.test(m[2])) {
      return { start: m.index, end: m.index + m[0].length, open: m[1], close: m[3] };
    }
    /* a rejected match is a WRAPPER whose lazy span swallowed the real region — resume
       just past its open tag so the tables nested inside it are still tried */
    CONTACT_IMAGE_REGION_RE_G.lastIndex = m.index + m[1].length;
  }
  return null;
}

/** the slot numbers 1..5 whose `Headshot<n>_Url` is populated, in order */
export function contactHeadshotSlots(get: TokLookup): number[] {
  const out: number[] = [];
  for (let n = 1; n <= 5; n++) if (get("Headshot" + n + "Url")) out.push(n);
  return out;
}

/** Layne's count table — "100 for 1–2, 90 for 3, 85 for 4, 80 for 5" */
export function contactHeadshotSize(count: number): number {
  if (count <= 2) return 100;
  if (count === 3) return 90;
  if (count === 4) return 85;
  return 80;
}

/* ── her fragments, verbatim ──────────────────────────────────────────────────────── */
const GROUP_ROW =
  '<tr><td align="center"><img alt="{{GroupImageAlt}}" border="0" height="{{GroupImageHeight}}" src="{{GroupImageUrl}}" style="display: block; border: 0px; width: {{GroupImageWidth}}px; max-width: 450px; height: auto; margin: 0 auto;" width="{{GroupImageWidth}}"></td></tr>';
const HEADSHOT_OPEN =
  '<tr><td align="center"><table align="center" border="0" cellpadding="0" cellspacing="0" role="presentation"><tr>';
/* ── HER CORRECTED FRAGMENT (MARKUP-CONTACT-US-TREATMENTS-20260902, revised 2026-09-08) ──
   Taken verbatim from CHB_Contact_Chicago_Headshot_LightSand_0226 (asset 997057). The
   2 September fragment had no border-radius and no VML, and every headshot in the DE is a
   square 220×220 JPG, so it rendered squares everywhere. Now: border-radius:50% rounds it
   outside Outlook, the v:oval with v:fill type="frame" rounds it IN Outlook, the 2px
   #DBD9D6 ring is stroke on the VML side and border on the CSS side, and — the trap — THE
   URL TOKEN APPEARS TWICE PER SLOT, once in v:fill src and once in img src. Both are
   substituted from the same column; doing one gives a blank circle in half the inboxes.
   inline-block, not block: that is what centres the img against the VML fallback. */
const HEADSHOT_TD =
  '<td align="center" valign="top" style="padding: 0 10px;">'
  + '<!--[if mso]><v:oval xmlns:v="urn:schemas-microsoft-com:vml" style="width:{{HeadshotSize}}px; height:{{HeadshotSize}}px; display:inline-block;" strokecolor="#DBD9D6" strokeweight="2px"><v:fill type="frame" src="{{HeadshotNUrl}}"/></v:oval><![endif]-->'
  + '<!--[if !mso]><!--><img alt="{{HeadshotNAlt}}" height="{{HeadshotSize}}" src="{{HeadshotNUrl}}" style="display: inline-block; border-radius: 50%; text-align: center; height: {{HeadshotSize}}px; width: {{HeadshotSize}}px; padding: 0px; border: 2px solid #DBD9D6;" width="{{HeadshotSize}}"> <!--<![endif]-->'
  + '</td>';
const HEADSHOT_CLOSE = "</tr></table></td></tr>";

/** substitute every `{{Token}}` in a fragment from `vals`, HTML-escaped */
function fill(frag: string, vals: Record<string, string>): string {
  return frag.replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (whole, k: string) =>
    Object.prototype.hasOwnProperty.call(vals, k) ? escML(vals[k]) : whole);
}

/** the region's new inner markup for a treatment, or null when the region should go */
export function contactRegionInner(treatment: ContactTreatment, get: TokLookup): string | null {
  if (treatment === "group") {
    const url = get("GroupImageUrl");
    if (!url) return null;
    const w = /^\d+$/.test(get("GroupImageWidth")) ? get("GroupImageWidth") : "";
    const h = /^\d+$/.test(get("GroupImageHeight")) ? get("GroupImageHeight") : "";
    let row = fill(GROUP_ROW, { GroupImageAlt: get("GroupImageAlt"), GroupImageUrl: url, GroupImageWidth: w, GroupImageHeight: h });
    /* ── A BLANK DIMENSION IS OMITTED, NEVER INVENTED (Layne, REPLY-…-20260902b) ────
       The first cut fell a blank width back to the fragment's 450 — but 450 is the
       CEILING, not a default: ATL's photo is 400 wide, and a blank width would have
       upscaled it 12% and soft. So a blank width drops both the attribute and the
       `width: …px;` declaration, and `max-width: 450px; height: auto` render the image
       at its natural size, capped — exactly what a blank height already did. */
    if (!h) row = row.replace(/ height=""/, "");
    if (!w) row = row.replace(/ width=""/, "").replace(/width: px; /, "");
    return row;
  }
  if (treatment === "headshots") {
    const slots = contactHeadshotSlots(get);
    if (!slots.length) return null;
    const size = String(contactHeadshotSize(slots.length));
    const tds = slots.map((n) =>
      fill(HEADSHOT_TD.split("HeadshotN").join("Headshot" + n), {
        ["Headshot" + n + "Alt"]: get("Headshot" + n + "Alt"),
        ["Headshot" + n + "Url"]: get("Headshot" + n + "Url"),
        HeadshotSize: size,
      }));
    return HEADSHOT_OPEN + tds.join("") + HEADSHOT_CLOSE;
  }
  return "";
}

/**
 * THE MARK OF A REWRITTEN REGION. Every markup this module writes carries it — inside
 * the region's open tag when the region is drawn, in place of the region when it is
 * hidden — so a later pass can tell OUR output from a pristine copy without comparing
 * markup: a block whose `__html` lacks the mark is untouched (a fresh SFMC re-pull
 * lands as `__html` and has no mark), and is therefore the base to cut from.
 */
export const CONTACT_TREATMENT_MARK = "<!-- ECH_CONTACT_TREATMENT -->";
export const contactTreated = (html: unknown): boolean => String(html || "").indexOf(CONTACT_TREATMENT_MARK) !== -1;

/**
 * ── SUBSTITUTION IS BLOCK-WIDE, NOT REGION-WIDE (BUG-CONTACTUS-TOKENS-NOT-RENDERED-20260909)
 * Layne: "Fed values from the division row show correctly in the side panel but never get
 * substituted into the rendered email… run the token substitution across the whole block,
 * not just inside marker regions."
 *
 * The tell in her report is the whole diagnosis: every token INSIDE a TREATMENT pair
 * rendered (the images, the sizes) and the only two OUTSIDE one — `{{ContactUrl}}` and
 * `{{Cta1Label}}` — both failed. Substitution used to live inside the `TREATMENT_RE`
 * callback, so its reach was the kept treatment's body and nothing else; the Contact Us
 * button sits in the block's shell, below the image region, and was never in scope. Every
 * division therefore shipped `href="{{ContactUrl}}"` — a dead link on the one element the
 * block exists for.
 *
 * So the pass runs ONCE, over the FINISHED markup, after the treatments have been chosen
 * and the empty slots dropped. Two properties that follow and must not be traded away:
 *   · ONCE, not per-region — a value that happens to contain `{{…}}` can never be
 *     re-entered as a token, because a single `String.replace` does not rescan what it
 *     inserted. Substituting per region and again block-wide would reopen that.
 *   · AFTER pruning — the deleted treatments and unpopulated slots are already gone, so
 *     their tokens are never resolved and never leak a headshot the row didn't answer.
 * `{{HeadshotSize}}` is the one exception and stays inside the marker pass: it is computed
 * from the SURVIVING slot count, which only that pass knows.
 */
const ALT_TOKEN_RE = /^headshot[1-5]alt$|^groupimagealt$/i;

export function contactSubstitute(html: string, get: TokLookup): string {
  return html.replace(/\{\{([A-Za-z0-9_]+)\}\}/g, (whole, k: string) => {
    /* ── AND THEN THE DECLARED DEFAULT (2026-09-09) ───────────────────────────────
       Found by the same test one field over: `Cta1Label` declares
       `default: "Contact Us"` and POR's `cta1_label` column is blank, so a row-only
       resolve left `{{Cta1Label}}` sitting in the button next to the href that had just
       been fixed. A blank column on a field that HAS a default is not a gap — the
       default is the answer. Only here, never in the slot/hide decisions above. */
    const v = get(k) || (get.def ? get.def(k) : "");
    /* an EMPTY answer is a real answer for the decorative alt tokens (they ship
       `alt=""` deliberately — see `contactAltGaps`); for anything else an unanswerable
       token is left as written, so a markup typo stays visible instead of silently
       rendering as nothing */
    return v || ALT_TOKEN_RE.test(k) ? escML(v) : whole;
  });
}

/**
 * The block's markup with the treatment applied AND its tokens filled. `base` is the
 * PRISTINE markup; the result is what `__html` should hold.
 *
 * A base this function did not change at all comes back byte-identical and WITHOUT the
 * mark — it is still pristine, and `echContactPatch` is entitled to keep treating it as
 * the base to cut from. The moment anything is rewritten (a region, a treatment, or a
 * single token) the mark goes on, because from then on the stored `__html0` is the only
 * pristine copy left and a re-cut must come from there.
 */
export function contactTreatmentHtml(base: string, get: TokLookup): string {
  const html = String(base || "");
  const shaped = contactTreatmentShape(html, get);
  const filled = contactSubstitute(shaped, get);
  if (filled === html) return html;               /* nothing rewritten → still pristine */
  return contactTreated(filled) ? filled : CONTACT_TREATMENT_MARK + filled;
}

/* ══════════════════════════════════════════════════════════════════════════════════
   `IFCOLUMN` — DELETE A REGION WHEN A NAMED COLUMN IS EMPTY (Layne, ASK-JACK-IFCOLUMN-20260909)
   ══════════════════════════════════════════════════════════════════════════════════
   Her ask, and her argument for the generic form over the specific one, which is the
   reason this is `IFCOLUMN` and not the `CTA:n` marker I wrote first:

     "Because the specific version buys one button and the generic version buys every
      conditional region we will ever need… Any future division-specific element — a
      second phone line, a licence disclosure in one state — becomes a Content Builder
      edit rather than a conversation with you."

   Same split as the treatments and the same rule as `SLOT:n`, with the column named
   explicitly instead of derived from a slot number:

     <!--IFCOLUMN:Cta2_Url-->  … markup using {{Cta2Label}} and {{Cta2Url}} …  <!--/IFCOLUMN:Cta2_Url-->

   Non-empty → keep the region, strip the markers, fill tokens as normal.
   Empty, or no such column → delete the region whole, markers included.

   ── WHY THIS IS A FIX AND NOT AN ENHANCEMENT ────────────────────────────────────────
   NEV is the only division with no web form. Its two CTAs — `Email Us` on a `mailto:`
   and `Call Us` on a `tel:` — are not a primary and a spare, they are the ONLY two ways
   to reach that division, and the block carries one button. Without this it ships half
   its contact options, in the block whose entire purpose is contact.

   ── THE COLUMN NAME IS THE KEY, AND IT RESOLVES ON THE EFFECTIVE VALUE ──────────────
   `Cta2_Url` normalises to `cta2url`, which is the row key `cta2_url` — so any column
   resolves by name with no declaration needed, which is what makes this a Content
   Builder edit. A requester's TYPED value is seen too: `contactLookup` now indexes a
   typed field under its declared `fromColumn` as well, so `IFCOLUMN:Cta1_Url` keeps its
   region for a division whose column is empty but whose author typed a link. Without
   that, `contacturl` and `cta1url` never meet and the region would vanish under them.

   ── SLOT COULD COLLAPSE INTO THIS, BUT NOT YET ──────────────────────────────────────
   She is right that `SLOT:1` is exactly `IFCOLUMN:Headshot1_Url`. It is deliberately
   left alone here: `SLOT` also feeds `contactHeadshotSlots`, which is what computes
   `{{HeadshotSize}}` from the surviving count, so collapsing it means moving the count
   as well. Worth doing as its own change, not folded into a fix NEV is waiting on. */
const IFCOL_ANY_RE = /<!--\s*IFCOLUMN:/i;
const IFCOL_RE = /<!--\s*IFCOLUMN:([A-Za-z0-9_]+)\s*-->([\s\S]*?)<!--\s*\/IFCOLUMN:\1\s*-->/gi;

/**
 * Keep the `IFCOLUMN` regions whose column has a value, delete the ones that do not.
 *
 * Runs on the SHELL, before the treatment work and before token substitution — a region
 * can sit anywhere, including outside every TREATMENT pair, which is the same fact that
 * produced the original token bug.
 */
export function contactIfColumnHtml(html: string, get: TokLookup): string {
  if (!IFCOL_ANY_RE.test(html)) return html;
  return html.replace(IFCOL_RE, (_whole, col: string, body: string) => {
    const v = get(col) || (get.def ? get.def(col) : "");
    return v.trim() ? body : "";
  });
}

/** the shaping half: pick the treatment / cut the image region, tokens still unfilled */
function contactTreatmentShape(html: string, get: TokLookup): string {
  html = contactIfColumnHtml(html, get);
  const treatment = contactTreatmentOf(get);
  /* the block that carries its own treatments decides its own markup — see below */
  if (TREATMENT_ANY_RE.test(html)) return contactTreatmentByMarkers(html, treatment, get);
  if (treatment === "none") return html;
  const r = contactImageRegion(html);
  if (!r) return html;
  const inner = contactRegionInner(treatment, get);
  const replacement = inner === null ? CONTACT_TREATMENT_MARK : r.open + CONTACT_TREATMENT_MARK + inner + r.close;
  return html.slice(0, r.start) + replacement + html.slice(r.end);
}

/* ══════════════════════════════════════════════════════════════════════════════════
   THE TREATMENTS LIVE IN THE BLOCK, THE CODE PICKS ONE (Layne's ask, 2026-09-08)
   ══════════════════════════════════════════════════════════════════════════════════
   BUG-CONTACTUS-RENDER-20260908: "Email formatting has to be something we can change in
   SFMC… Put all three treatments in our block, delimited by marker comments. Your code
   keeps the one matching Image_Treatment and deletes the other two. Selection, slot-hiding
   and sizing by count stay yours." Same split as ECH_LIST_CTALabels: she owns the markup,
   this side owns the choice. The fragments hard-coded above become the FALLBACK for a
   block that has not adopted the markers yet.

   THE SYNTAX (the answer to "tell us the marker syntax you want"):

     <!--TREATMENT:none-->      … the 80×53 icon table …                <!--/TREATMENT:none-->
     <!--TREATMENT:group-->     … group image markup …                  <!--/TREATMENT:group-->
     <!--TREATMENT:headshots--> … <!--SLOT:1--> <td>…</td> <!--/SLOT:1-->
                                  <!--SLOT:2--> <td>…</td> <!--/SLOT:2--> … <!--/TREATMENT:headshots-->

   · The three TREATMENT pairs may sit anywhere in the block, each wrapping whatever markup
     that treatment needs (a whole `margin-bottom: 15px` table is the natural unit). Exactly
     one survives — the one named by Image_Treatment — with its markers removed; the other
     two are deleted whole, markers included.
   · Inside `headshots`, each cell sits in a SLOT pair numbered 1–5. A slot whose
     `Headshot<n>Url` resolves empty is deleted whole — both conditional branches, never an
     empty `<td>` — so the row re-centres on the survivors.
   · Tokens are hers, filled by name as today: `{{Headshot<n>Url}}` (twice per cell, VML +
     img), `{{Headshot<n>Alt}}`, `{{GroupImageUrl/Alt/Width/Height}}`, and `{{HeadshotSize}}`,
     which this side substitutes from the populated count (100/100/90/85/80).
   · Empty means hide, unchanged: `group` with no URL, or `headshots` with no populated slot,
     deletes that treatment too and renders nothing — never the icon in its place.
   · A token the row cannot answer is left as written so a markup typo stays visible.

   A block with no TREATMENT markers takes the fragment path above, exactly as before, so
   the two can coexist while she migrates. */
const TREATMENT_ANY_RE = /<!--\s*TREATMENT:(none|group|headshots)\s*-->/i;
const TREATMENT_RE = /<!--\s*TREATMENT:(none|group|headshots)\s*-->([\s\S]*?)<!--\s*\/TREATMENT:\1\s*-->/gi;
const SLOT_RE = /<!--\s*SLOT:([1-5])\s*-->([\s\S]*?)<!--\s*\/SLOT:\1\s*-->/gi;

/**
 * SELECTION AND PRUNING ONLY — the tokens are left as written. `contactTreatmentHtml`
 * fills them block-wide afterwards (BUG-CONTACTUS-TOKENS-NOT-RENDERED-20260909); doing it
 * here as well is what confined substitution to the marker regions and shipped a dead
 * `{{ContactUrl}}` in every division's button. `{{HeadshotSize}}` is the exception: it is
 * derived from the count of SURVIVING slots, which is known here and nowhere else.
 */
export function contactTreatmentByMarkers(html: string, treatment: ContactTreatment, get: TokLookup): string {
  const slots = contactHeadshotSlots(get);
  const size = String(contactHeadshotSize(slots.length));
  /* nothing to draw → the chosen treatment is deleted along with the other two */
  const hide = (treatment === "group" && !get("GroupImageUrl")) || (treatment === "headshots" && !slots.length);
  let marked = false;
  const out = html.replace(TREATMENT_RE, (_whole, name: string, body: string) => {
    if (name.toLowerCase() !== treatment || hide) return "";
    let kept = body;
    if (treatment === "headshots") {
      kept = kept.replace(SLOT_RE, (_w, n: string, cell: string) => (slots.indexOf(+n) > -1 ? cell : ""));
    }
    kept = kept.replace(/\{\{HeadshotSize\}\}/g, size);
    marked = true;
    return CONTACT_TREATMENT_MARK + kept;
  });
  /* a base whose every treatment was deleted still needs the mark, so the next pass knows
     this is our output and re-cuts from the stored pristine copy */
  return marked ? out : CONTACT_TREATMENT_MARK + out;
}

/**
 * The alt-text gaps the block-wide warnings list should show — one sentence per kind,
 * counted, never blocking. Empty when the treatment draws nothing or every image has alt.
 */
export function contactAltGaps(_get: TokLookup): string[] {
  /* ── NO WARNING: THE IMAGES ARE DECORATIVE BY DESIGN (Layne, 2026-09-08) ────────────
     "Headshots and group photos are decorative and carry alt="" deliberately… the live
     CHB block ships alt="" on all five headshots today, so production already treats
     them this way — we were flagging approved behaviour as a defect." The attribute
     stays PRESENT and EMPTY (a missing alt can make a reader announce the filename),
     which `alt="{{HeadshotNAlt}}"` against an empty column produces. The 2 September
     ruling this replaced ("render it, and warn") is withdrawn by the same author. */
  return [];
}
